const safeJsonParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const toTime = (value) => {
  const parsed = Date.parse(value || '');
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeEmailSegment = (email) =>
  encodeURIComponent(String(email || '').trim().toLowerCase());

export const getGreResultMetaKey = (email, testId) =>
  `gre_result_meta_${normalizeEmailSegment(email)}_${testId}`;

export const getGreLegacyDetailedResultsKey = (testId) =>
  `${testId}-detailed-results`;

export const getGreLegacyCompletionDateKey = (testId) =>
  `${testId}-completion-date`;

export const getGreDetailedResultsKey = (testId, email) =>
  email
    ? `gre_detailed_results_${normalizeEmailSegment(email)}_${testId}`
    : getGreLegacyDetailedResultsKey(testId);

export const getGreCompletionDateKey = (testId, email) =>
  email
    ? `gre_completion_date_${normalizeEmailSegment(email)}_${testId}`
    : getGreLegacyCompletionDateKey(testId);

export const getGreResultCacheKey = (email, testId) =>
  `test_results_${normalizeEmailSegment(email)}_${testId}`;

export const getGreResultCacheTimestampKey = (email, testId) =>
  `${getGreResultCacheKey(email, testId)}_timestamp`;

export const getGrePendingSyncKey = (email) =>
  `gre_pending_sync_${normalizeEmailSegment(email)}`;

export const normalizeGreTestSummary = (raw = {}) => {
  const completedAt =
    raw.completed_at || raw.end_time || raw.completedAt || raw.created_at || null;
  const updatedAt = raw.updated_at || raw.updatedAt || completedAt;
  const version = raw.version || updatedAt || completedAt;

  return {
    testId: raw.test_id || raw.id || raw.mock_test_id,
    dbId: raw.db_id ?? raw.id ?? null,
    status: raw.status,
    completedAt,
    updatedAt,
    version,
    verbalScore: raw.verbal_score ?? null,
    quantitativeScore: raw.quantitative_score ?? raw.quant_score ?? null,
    awaScore: raw.awa_score ?? null,
    totalScore: raw.total_score ?? raw.scaled_score ?? null,
    totalAttempts: raw.total_attempts ?? 1,
  };
};

export const normalizeGreTestSummaryPayload = (data = {}) => {
  if (!Array.isArray(data?.tests)) return [];
  return data.tests.map(normalizeGreTestSummary).filter((item) => item.testId);
};

export const getGreResultMeta = (email, testId) => {
  if (!email || !testId) return null;
  return safeJsonParse(localStorage.getItem(getGreResultMetaKey(email, testId)));
};

export const setGreResultMeta = (email, testId, meta) => {
  if (!email || !testId || !meta) return;
  localStorage.setItem(
    getGreResultMetaKey(email, testId),
    JSON.stringify({
      testId,
      ...meta,
      cachedAt: meta.cachedAt || Date.now(),
    })
  );
};

export const isSummaryNewerThanMeta = (summary, meta) => {
  if (!summary?.version) return false;
  if (!meta?.version) return true;
  return toTime(summary.version) > toTime(meta.version);
};

export const writeGreSummaryToLocalCache = (email, summary) => {
  if (!email || !summary?.testId) return null;

  const completionDate = summary.completedAt || summary.updatedAt;
  if (completionDate) {
    localStorage.setItem(
      getGreCompletionDateKey(summary.testId, email),
      completionDate
    );
  }

  const meta = {
    dbId: summary.dbId,
    version: summary.version,
    completedAt: completionDate,
    source: 'backend',
    pendingSync: false,
  };
  setGreResultMeta(email, summary.testId, meta);
  return meta;
};

export const markGreLocalOnlyResult = (email, testId, completionDate) => {
  if (!email || !testId || getGreResultMeta(email, testId)) return;
  setGreResultMeta(email, testId, {
    version: completionDate,
    completedAt: completionDate,
    source: 'local_only',
    pendingSync: true,
  });
};

export const writeGreDetailedResultToLocalCache = ({
  email,
  testId,
  results,
  version,
}) => {
  if (!email || !testId || !results?.success || !results?.test) return null;

  const cacheKey = getGreResultCacheKey(email, testId);
  const cacheTimestampKey = getGreResultCacheTimestampKey(email, testId);
  const cachedPayload = {
    ...results,
    _cached_test_id: testId,
  };
  localStorage.setItem(cacheKey, JSON.stringify(cachedPayload));
  localStorage.setItem(cacheTimestampKey, Date.now().toString());

  const detailed = results.test.detailed_results || {};
  const enhancedScores = detailed.enhanced_scoring || null;
  const userAnswersRaw = results.test.user_answers || {};
  const { essay_text: essayText = '', ...userAnswers } = userAnswersRaw;
  const completionDate =
    results.test.end_time || results.test.created_at || results.test.completed_at;
  const localStorageResults = {
    testId,
    completionDate,
    enhancedScores,
    awaFeedback: enhancedScores?.awa?.feedback || null,
    userAnswers,
    essayText: essayText || userAnswersRaw.essay_test || '',
    questionTimings: detailed.question_timings || {},
    sectionResults: detailed.section_results || {},
    overallResults: detailed.overall_performance || null,
  };

  localStorage.setItem(
    getGreDetailedResultsKey(testId, email),
    JSON.stringify(localStorageResults)
  );

  if (completionDate) {
    localStorage.setItem(getGreCompletionDateKey(testId, email), completionDate);
  }

  const meta = {
    dbId: results.test.db_id,
    version:
      version ||
      results.test.updated_at ||
      results.test.end_time ||
      results.test.created_at ||
      completionDate,
    completedAt: completionDate,
    source: 'backend',
    pendingSync: false,
  };
  setGreResultMeta(email, testId, meta);
  return meta;
};

export const readPendingGreSyncQueue = (email) => {
  if (!email) return [];
  const parsed = safeJsonParse(localStorage.getItem(getGrePendingSyncKey(email)));
  return Array.isArray(parsed) ? parsed : [];
};

export const writePendingGreSyncQueue = (email, queue) => {
  if (!email) return;
  localStorage.setItem(getGrePendingSyncKey(email), JSON.stringify(queue || []));
};

export const enqueuePendingGreSync = (email, item) => {
  if (!email || !item?.testId) return;
  const queue = readPendingGreSyncQueue(email).filter(
    (entry) => entry.testId !== item.testId
  );
  queue.push({
    ...item,
    queuedAt: item.queuedAt || Date.now(),
  });
  writePendingGreSyncQueue(email, queue);
};

export const readGreDetailedResultFromLocalCache = (email, testId) => {
  if (!email || !testId) return null;
  return safeJsonParse(localStorage.getItem(getGreDetailedResultsKey(testId, email)));
};

export const readGreCompletionDateFromLocalCache = (email, testId) => {
  if (!email || !testId) return null;
  return localStorage.getItem(getGreCompletionDateKey(testId, email));
};
